import { booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, inject, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';

/**
 * ara-checkbox — dünner Ersatz für `mat-checkbox`. Funktion, API und Look sind bewusst identisch zum
 * Material-Standard (Drop-in: `[checked]`, `[disabled]`, `[indeterminate]`, `color`, `labelPosition`,
 * `(change)` mit {@link MatCheckboxChange}, plus volle Reactive-/Template-Forms-Unterstützung als
 * ControlValueAccessor). Als zentraler Wrapper — Pendant zu {@link AraSlideToggleComponent} —, damit
 * projektweites Checkbox-Styling später an EINER Stelle passiert.
 */
@Component({
	selector: 'ara-checkbox',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatCheckboxModule],
	templateUrl: './ara-checkbox.component.html',
	styleUrl: './ara-checkbox.component.scss',
	providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AraCheckboxComponent), multi: true }],
})
export class AraCheckboxComponent implements ControlValueAccessor {
	private readonly cdr = inject(ChangeDetectorRef);

	@Input({ transform: booleanAttribute }) checked = false;
	@Input({ transform: booleanAttribute }) disabled = false;
	@Input({ transform: booleanAttribute }) indeterminate = false;
	@Input() color: ThemePalette;
	@Input() name: string | null = null;
	@Input() value: string | undefined;
	@Input() labelPosition: 'before' | 'after' = 'after';
	@Input({ transform: booleanAttribute }) disableRipple = false;
	@Input({ transform: booleanAttribute }) required = false;
	@Input('aria-label') ariaLabel: string | null = null;

	// eslint-disable-next-line @angular-eslint/no-output-native -- spiegelt bewusst die Material-Checkbox-API (change)
	@Output() readonly change = new EventEmitter<MatCheckboxChange>();
	@Output() readonly indeterminateChange = new EventEmitter<boolean>();

	private onChange: (value: boolean) => void = () => {};
	protected onTouched: () => void = () => {};

	protected onInnerChange(event: MatCheckboxChange): void {
		this.checked = event.checked;
		this.onChange(event.checked);
		this.onTouched();
		this.change.emit(event);
	}

	protected onIndeterminate(value: boolean): void {
		this.indeterminate = value;
		this.indeterminateChange.emit(value);
	}

	writeValue(value: boolean): void {
		this.checked = !!value;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: boolean) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}
}
